// Admin → Countries: staff's rules per country (apps/api/src/routes/admin/countries.ts). For each
// country: whether GaintPlay is open there (open, no new sign-ups, blocked), the order of the offer
// walls and surveys on Earn, and the networks not offered there. Rules follow where a request comes
// from; "Blocked" also covers members whose account is from the country. A change reaches the site
// within a minute.
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Ban, Globe2, RotateCcw, UserX } from 'lucide-react';
import { COUNTRY_CODES } from '@gaintplay/core/countries';
import { arrangeForCountry } from '@gaintplay/core/country-rules';
import { api } from '../lib/api';
import {
  confirmAction,
  Drawer,
  EmptyState,
  ErrorNote,
  fmtNum,
  Initial,
  PageHeader,
  Pill,
  SearchInput,
  SkeletonRows,
  Tabs,
  TimeAgo,
  toast,
  ViewOnly,
} from '../components/ui';

/* ------------------------------------------------------------------ Shapes */

type Access = 'open' | 'no_signups' | 'blocked';

interface Rule {
  country: string;
  access: Access;
  networkOrder: string[] | null;
  hiddenNetworks: string[] | null;
  note: string | null;
  updatedAt: number;
  updatedBy: string | null;
  updatedByName: string | null;
}

interface Network {
  id: string;
  name: string;
  kind: string;
  status: 'active' | 'paused' | 'disabled';
  logoUrl: string | null;
  wall: boolean;
  offers: boolean;
  order: number;
}

interface Data {
  rules: Rule[];
  networks: Network[];
  members: { code: string; n: number }[];
  canEdit: boolean;
}

/* ------------------------------------------------------------------ Helpers */

const names = new Intl.DisplayNames(['en'], { type: 'region' });
const countryName = (code: string) => {
  try {
    return names.of(code) ?? code;
  } catch {
    return code;
  }
};
/** 🇳🇵 from "NP": two regional indicator letters. */
const flag = (code: string) =>
  String.fromCodePoint(...[...code].map((ch) => 0x1f1a5 + ch.charCodeAt(0)));

const ACCESS: Record<Access, { label: string; tone: 'mint' | 'sun' | 'berry'; text: string }> = {
  open: { label: 'Open', tone: 'mint', text: 'Everything works as usual.' },
  no_signups: {
    label: 'No new sign-ups',
    tone: 'sun',
    text: 'Sign-ups from here are refused. Members already here keep earning and cashing out.',
  },
  blocked: {
    label: 'Blocked',
    tone: 'berry',
    text: 'Nobody here can sign up, sign in or use their account, and neither can members whose account is from here, wherever they are. They see a notice with the support email. Credits networks send for them still arrive.',
  },
};

const KIND: Record<string, string> = {
  offerwall: 'Offer wall',
  survey: 'Surveys',
  video: 'Videos',
  game: 'Games',
  cpa: 'Offers',
  internal: 'Internal',
};

/** A country's Earn list as members there see it: the networks with a wall, in its order. */
function wallOrder(networks: Network[], rule: Pick<Rule, 'networkOrder'> | null): string[] {
  const walls = networks.filter((n) => n.wall);
  return arrangeForCountry(walls, {
    access: 'open',
    networkOrder: rule?.networkOrder ?? null,
    hiddenNetworks: null,
  }).map((n) => n.id);
}

function earnSummary(rule: Rule | undefined): string | null {
  if (!rule) return null;
  const parts: string[] = [];
  if (rule.networkOrder?.length) parts.push('Own order');
  if (rule.hiddenNetworks?.length) parts.push(`${rule.hiddenNetworks.length} hidden`);
  return parts.length ? parts.join(' · ') : null;
}

/* ------------------------------------------------------------------ Page */

export function Countries() {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['countries'],
    queryFn: () => api<Data>('/countries'),
  });
  const [tab, setTab] = useState<'rules' | 'all'>('rules');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const rules = useMemo(() => new Map((data?.rules ?? []).map((r) => [r.country, r])), [data]);
  const members = useMemo(() => new Map((data?.members ?? []).map((m) => [m.code, m.n])), [data]);
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const codes = tab === 'rules' && !term ? [...rules.keys()] : [...COUNTRY_CODES];
    return codes
      .filter(
        (c) => !term || c.toLowerCase() === term || countryName(c).toLowerCase().includes(term),
      )
      .sort(
        (a, b) =>
          (members.get(b) ?? 0) - (members.get(a) ?? 0) ||
          countryName(a).localeCompare(countryName(b)),
      );
  }, [q, tab, rules, members]);

  const counts = {
    blocked: data?.rules.filter((r) => r.access === 'blocked').length ?? 0,
    closed: data?.rules.filter((r) => r.access === 'no_signups').length ?? 0,
    earn: data?.rules.filter((r) => earnSummary(r)).length ?? 0,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Countries"
        description="Where GaintPlay is open, and which offer walls and surveys members see first in each country. Rules follow where a visitor is; a change reaches the site within a minute."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat icon={Ban} label="Blocked" value={counts.blocked} hint="Closed to everyone there" />
        <Stat
          icon={UserX}
          label="No new sign-ups"
          value={counts.closed}
          hint="Members there carry on"
        />
        <Stat
          icon={Globe2}
          label="Own Earn list"
          value={counts.earn}
          hint="Their own order or hidden networks"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          label="Which countries"
          value={tab}
          onChange={setTab}
          items={[
            { key: 'rules', label: 'With rules', count: data?.rules.length },
            { key: 'all', label: 'All countries', count: COUNTRY_CODES.length },
          ]}
        />
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Find a country or code"
          className="w-full sm:ml-auto sm:w-72"
        />
      </div>

      <section className="card overflow-hidden">
        {isLoading ? (
          <SkeletonRows rows={8} />
        ) : error || !data ? (
          <div className="p-4 sm:p-5">
            <ErrorNote error={error ?? new Error('No answer.')} onRetry={() => void refetch()} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Globe2}
            title={
              tab === 'rules' && !q
                ? 'Every country is open, in the usual order'
                : 'No country matches'
            }
            action={
              tab === 'rules' && !q ? (
                <button
                  type="button"
                  className="btn-ghost h-10 px-4 py-0 text-[14px]"
                  onClick={() => setTab('all')}
                >
                  Set rules for a country
                </button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-hairline">
            <li className="hidden grid-cols-[minmax(0,1.6fr)_7rem_9rem_minmax(0,1fr)_9rem] gap-4 px-5 py-2.5 text-[12px] font-semibold text-stone md:grid">
              <span>Country</span>
              <span className="text-right">Members</span>
              <span>GaintPlay</span>
              <span>Earn list</span>
              <span>Changed</span>
            </li>
            {rows.map((code) => {
              const r = rules.get(code);
              const access = r?.access ?? 'open';
              const summary = earnSummary(r);
              return (
                <li key={code}>
                  <button
                    type="button"
                    onClick={() => setOpen(code)}
                    className="press-row grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 px-4 py-3 text-left md:grid-cols-[minmax(0,1.6fr)_7rem_9rem_minmax(0,1fr)_9rem] md:px-5"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <span aria-hidden="true" className="text-[22px] leading-none">
                        {flag(code)}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-[15px] font-semibold">
                          {countryName(code)}
                        </span>
                        <span className="text-[12px] text-stone md:hidden">
                          {code} · {fmtNum(members.get(code) ?? 0)} members
                          {summary ? ` · ${summary}` : ''}
                        </span>
                        <span className="hidden text-[12px] text-stone md:inline">{code}</span>
                      </span>
                    </span>
                    <span className="hidden text-right text-[14px] tabular-nums md:block">
                      {fmtNum(members.get(code) ?? 0)}
                    </span>
                    <span className="justify-self-end md:justify-self-start">
                      <Pill tone={ACCESS[access].tone}>{ACCESS[access].label}</Pill>
                    </span>
                    <span className="hidden text-[14px] text-graphite md:block">
                      {summary ?? <span className="text-stone">Usual</span>}
                    </span>
                    <span className="hidden text-[13px] text-stone md:block">
                      {r ? (
                        <>
                          <TimeAgo t={r.updatedAt} />
                          {r.updatedByName ? ` · ${r.updatedByName}` : ''}
                        </>
                      ) : (
                        '—'
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {open && data && (
        <CountryEditor
          key={open}
          code={open}
          rule={rules.get(open) ?? null}
          networks={data.networks}
          members={members.get(open) ?? 0}
          canEdit={data.canEdit}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Ban;
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div className="card flex items-center gap-3 p-4">
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-ink/[0.05]">
        <Icon size={18} aria-hidden="true" strokeWidth={1.75} />
      </span>
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-stone">{label}</span>
        <span className="block text-[22px] font-bold leading-tight tabular-nums">{value}</span>
        <span className="block truncate text-[12px] text-stone">{hint}</span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ Editor */

function CountryEditor({
  code,
  rule,
  networks,
  members,
  canEdit,
  onClose,
}: {
  code: string;
  rule: Rule | null;
  networks: Network[];
  members: number;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const usual = useMemo(() => wallOrder(networks, null), [networks]);
  const [access, setAccess] = useState<Access>(rule?.access ?? 'open');
  const [order, setOrder] = useState<string[]>(() => wallOrder(networks, rule));
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(rule?.hiddenNetworks ?? []));
  const [note, setNote] = useState(rule?.note ?? '');
  const [busy, setBusy] = useState(false);
  const byId = useMemo(() => new Map(networks.map((n) => [n.id, n])), [networks]);
  const offersOnly = networks.filter((n) => n.offers && !n.wall);
  const name = countryName(code);
  const ownOrder = order.join() !== usual.join();

  const initial = JSON.stringify({
    access: rule?.access ?? 'open',
    order: wallOrder(networks, rule),
    hidden: [...(rule?.hiddenNetworks ?? [])].sort(),
    note: rule?.note ?? '',
  });
  const changed =
    JSON.stringify({ access, order, hidden: [...hidden].sort(), note: note.trim() }) !== initial;

  const move = (i: number, by: -1 | 1) =>
    setOrder((o) => {
      const j = i + by;
      if (j < 0 || j >= o.length) return o;
      const next = [...o];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  const toggle = (id: string, show: boolean) =>
    setHidden((h) => {
      const next = new Set(h);
      if (show) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    if (access === 'blocked' && rule?.access !== 'blocked') {
      const ok = await confirmAction({
        title: `Block GaintPlay in ${name}?`,
        body: (
          <>
            Nobody in {name} can sign up, sign in or use their account any more, and neither can the{' '}
            {fmtNum(members)} member{members === 1 ? '' : 's'} whose account is from {name},
            wherever they are. They see a notice with the support email instead of the site.
          </>
        ),
        confirmLabel: `Block ${name}`,
        tone: 'danger',
      });
      if (ok === null) return;
    }
    setBusy(true);
    try {
      const r = await api<{ ok: true; removed: boolean }>(`/countries/${code}`, {
        method: 'PUT',
        body: {
          access,
          networkOrder: ownOrder ? order : [],
          hiddenNetworks: [...hidden],
          note: note.trim(),
        },
      });
      await qc.invalidateQueries({ queryKey: ['countries'] });
      toast(
        r.removed
          ? `${name} is back to the usual: open, usual order. Live within a minute.`
          : `${name} saved. Live within a minute.`,
      );
      onClose();
    } catch (e) {
      toast((e as Error).message, 'error');
      setBusy(false);
    }
  };

  return (
    <Drawer
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <span aria-hidden="true">{flag(code)}</span> {name}
        </span>
      }
      subtitle={`${code} · ${fmtNum(members)} member${members === 1 ? '' : 's'}`}
      footer={
        canEdit ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`btn-primary h-11 px-5 py-0 text-[15px] ${access === 'blocked' && rule?.access !== 'blocked' ? 'bg-berry-deep hover:bg-berry-deep/90' : ''}`}
              disabled={busy || !changed}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn-ghost h-11 px-5 py-0 text-[15px]"
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-7">
        {!canEdit && (
          <ViewOnly>Changing country rules needs the “Change country rules” permission.</ViewOnly>
        )}

        <Section title="GaintPlay here">
          <div role="radiogroup" aria-label={`GaintPlay in ${name}`} className="grid gap-2">
            {(Object.keys(ACCESS) as Access[]).map((a) => {
              const on = access === a;
              return (
                <button
                  key={a}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  disabled={!canEdit}
                  onClick={() => setAccess(a)}
                  className={`rounded-[12px] border px-4 py-3 text-left transition disabled:cursor-default ${
                    on
                      ? a === 'blocked'
                        ? 'border-berry-deep bg-berry/10'
                        : 'border-ink bg-ink/[0.04]'
                      : 'border-hairline hover:border-ink/40'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={`grid h-4 w-4 place-items-center rounded-full border-2 ${on ? 'border-ink' : 'border-ink/30'}`}
                    >
                      {on && <span className="h-1.5 w-1.5 rounded-full bg-ink" />}
                    </span>
                    <span className="text-[15px] font-bold">{ACCESS[a].label}</span>
                  </span>
                  <span className="mt-1 block pl-6 text-[13px] leading-snug text-graphite">
                    {ACCESS[a].text}
                  </span>
                </button>
              );
            })}
          </div>
        </Section>

        <Section
          title="Offer walls and surveys on Earn"
          hint="Members here see them in this order, first at the top. Hidden ones aren’t listed and won’t open; hiding a network hides its offers too."
          action={
            canEdit && ownOrder ? (
              <button
                type="button"
                className="btn-ghost h-9 gap-1.5 px-3 py-0 text-[13px]"
                onClick={() => setOrder(usual)}
              >
                <RotateCcw size={14} aria-hidden="true" /> Usual order
              </button>
            ) : null
          }
        >
          {order.length === 0 ? (
            <p className="text-[14px] text-stone">No network has an offer wall yet.</p>
          ) : (
            <ol className="divide-y divide-hairline rounded-[12px] border border-hairline">
              {order.map((id, i) => {
                const n = byId.get(id)!;
                const shown = !hidden.has(id);
                return (
                  <li
                    key={id}
                    className={`flex items-center gap-3 px-3 py-2.5 ${shown ? '' : 'bg-ink/[0.03]'}`}
                  >
                    <span className="w-6 shrink-0 text-center text-[13px] font-bold tabular-nums text-stone">
                      {shown ? i + 1 - order.slice(0, i).filter((x) => hidden.has(x)).length : '–'}
                    </span>
                    <Logo network={n} />
                    <span className={`min-w-0 flex-1 ${shown ? '' : 'opacity-50'}`}>
                      <span className="block truncate text-[14px] font-semibold">{n.name}</span>
                      <span className="flex flex-wrap gap-x-2 text-[12px] text-stone">
                        {KIND[n.kind] ?? n.kind}
                        {n.offers && <span>· with offers</span>}
                        {n.status !== 'active' && (
                          <span className="text-berry-deep">· {n.status}</span>
                        )}
                      </span>
                    </span>
                    {canEdit && (
                      <span className="flex shrink-0 gap-1">
                        <IconButton
                          label={`Move ${n.name} up`}
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                        >
                          <ArrowUp size={15} aria-hidden="true" />
                        </IconButton>
                        <IconButton
                          label={`Move ${n.name} down`}
                          disabled={i === order.length - 1}
                          onClick={() => move(i, 1)}
                        >
                          <ArrowDown size={15} aria-hidden="true" />
                        </IconButton>
                      </span>
                    )}
                    <ShowSwitch
                      on={shown}
                      disabled={!canEdit}
                      label={n.name}
                      onChange={(v) => toggle(id, v)}
                    />
                  </li>
                );
              })}
            </ol>
          )}
        </Section>

        {offersOnly.length > 0 && (
          <Section
            title="Offers on Earn"
            hint="Networks whose offers appear on Earn one by one. Hidden ones show none here."
          >
            <ul className="divide-y divide-hairline rounded-[12px] border border-hairline">
              {offersOnly.map((n) => (
                <li key={n.id} className="flex items-center gap-3 px-3 py-2.5">
                  <Logo network={n} />
                  <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">
                    {n.name}
                  </span>
                  <ShowSwitch
                    on={!hidden.has(n.id)}
                    disabled={!canEdit}
                    label={n.name}
                    onChange={(v) => toggle(n.id, v)}
                  />
                </li>
              ))}
            </ul>
          </Section>
        )}

        <Section
          title="Note for staff"
          hint="Why this country is set this way. Members never see it."
        >
          <textarea
            className="input min-h-[80px] w-full text-[14px]"
            maxLength={500}
            value={note}
            disabled={!canEdit}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Most sign-ups from here are on VPNs; AdGem doesn’t pay here."
          />
        </Section>

        {rule && (
          <p className="text-[12px] text-stone">
            Last changed <TimeAgo t={rule.updatedAt} />
            {rule.updatedByName ? ` by ${rule.updatedByName}` : ''}.
          </p>
        )}
      </div>
    </Drawer>
  );
}

function Section({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-end gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-bold">{title}</h3>
          {hint && <p className="mt-0.5 text-[13px] leading-snug text-stone">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Logo({ network }: { network: Network }) {
  return network.logoUrl ? (
    <img
      src={network.logoUrl}
      alt=""
      width={32}
      height={32}
      className="h-8 w-8 shrink-0 rounded-[8px] border border-hairline bg-paper object-contain"
    />
  ) : (
    <Initial name={network.name} size={32} />
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="btn-ghost h-9 w-9 p-0 disabled:opacity-30"
    >
      {children}
    </button>
  );
}

function ShowSwitch({
  on,
  disabled,
  label,
  onChange,
}: {
  on: boolean;
  disabled: boolean;
  label: string;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={`Show ${label} here`}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="group inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full disabled:cursor-default"
    >
      <span
        className={`relative h-6 w-10 shrink-0 rounded-full transition ${on ? 'bg-mint' : 'bg-ink/25 group-hover:bg-ink/35'}`}
      >
        <span
          className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(17,17,16,0.25)] transition-all ${on ? 'left-5' : 'left-1'}`}
        />
      </span>
      <span
        className={`hidden w-12 text-left text-[13px] font-bold sm:inline ${on ? 'text-mint-deep' : 'text-stone'}`}
      >
        {on ? 'Shown' : 'Hidden'}
      </span>
    </button>
  );
}
